const BLUE = "#3f6edb";
const DARK_BLUE = "#1f4e78";
const LIGHT_BLUE = "#eef4ff";
const BORDER = "#d9e2f3";
const GREEN = "#008060";
const TEXT = "#1f2937";

const toNumberOrZero = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toNumberOrNull = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const escapeHtml = (value) => {
  if (value == null) return "";

  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
};

const formatNumber = (value, decimals = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";

  return n.toLocaleString("es-AR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatPercent = (value, decimals = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";

  return `${n.toLocaleString("es-AR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
};

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0,00";

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDateOnly = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("es-AR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const translateFrequency = (value) => {
  const freq = String(value || "").toLowerCase();

  const map = {
    hour: "Hora",
    hourly: "Hora",
    day: "Día",
    daily: "Día",
    week: "Semana",
    weekly: "Semana",
    month: "Mes",
    monthly: "Mes",
    year: "Año",
    yearly: "Año",
    minute: "Minuto",
    minutes: "Minutos",
    "15m": "Cada 15 minutos",
    "30m": "Cada 30 minutos",
    "60m": "Hora",
  };

  return map[freq] || value || "-";
};

const getField = (item, candidates) => {
  if (!item) return null;

  for (const field of candidates) {
    if (item[field] != null) return item[field];
  }

  return null;
};

const translateOrigin = (origin) => {
  const value = String(origin || "").toLowerCase();

  if (value === "statistical") return "Estadístico";
  if (value === "predictive") return "Predictivo";
  if (value === "combined") return "Combinado";

  return origin || "-";
};

const translatePeriodType = (value) => {
  const period = String(value || "").toUpperCase();

  const map = {
    PRODUCTIVO: "Horario productivo",
    NO_PRODUCTIVO: "Fuera de horario",
    PRE_OPERATIVO: "Transición previa",
    POST_OPERATIVO: "Transición posterior",
    HORA_EXTRA: "Hora extra autorizada",
    SUGGESTED_OVERTIME: "Posible hora extra",
    HOLIDAY: "Feriado",
    SPECIAL_CLOSED: "Día no laborable",
    SPECIAL_WORKING: "Día laboral especial",
  };

  return map[period] || value || "-";
};

const translateAnomalyLevel = (value) => {
  const level = String(value || "").toLowerCase();

  if (level === "normal") return "Normal";
  if (level === "revisar") return "Revisar";
  if (level === "anomalo" || level === "anómalo") return "Anómalo";
  if (level === "critico" || level === "crítico") return "Crítico";

  return value || "-";
};

const getPassiveLevel = (pct) => {
  const value = toNumberOrZero(pct);

  if (value <= 10) return "Muy bueno";
  if (value <= 20) return "Normal/controlado";
  if (value <= 35) return "Revisable";
  if (value <= 50) return "Alto";

  return "Crítico";
};

const isElevatedNonProductivePoint = (point) => {
  if (!point || point.period_type !== "NO_PRODUCTIVO") return false;

  const level = String(point.anomaly_level || "").toLowerCase();

  return (
    point.is_near_base_load === false ||
    level === "anómalo" ||
    level === "anomalo" ||
    level === "crítico" ||
    level === "critico"
  );
};

const getVariationLabel = (stdDev, mean) => {
  const std = Number(stdDev);
  const avg = Number(mean);

  if (!Number.isFinite(std) || !Number.isFinite(avg) || avg <= 0) {
    return "Sin variación relevante";
  }

  const ratio = std / avg;

  if (ratio < 0.25) return "Baja";
  if (ratio < 0.75) return "Media";
  return "Alta";
};

const tableStyles = `
  <style>
    body {
      font-family: Arial, sans-serif;
      color: ${TEXT};
      background: #ffffff;
    }

    .report-title {
      background: ${DARK_BLUE};
      color: #ffffff;
      font-size: 18px;
      font-weight: bold;
      padding: 10px;
      border: 1px solid ${DARK_BLUE};
    }

    .section-title {
      background: ${LIGHT_BLUE};
      color: ${DARK_BLUE};
      font-size: 14px;
      font-weight: bold;
      padding: 8px;
      border: 1px solid ${BORDER};
      margin-top: 18px;
    }

    .note {
      color: #475569;
      font-size: 12px;
      padding: 7px;
      border: 1px solid ${BORDER};
      background: #f8fafc;
      margin-bottom: 12px;
    }

    table {
      border-collapse: collapse;
      width: 100%;
      margin-bottom: 18px;
    }

    th {
      background: ${BLUE};
      color: #ffffff;
      font-weight: bold;
      border: 1px solid ${BLUE};
      padding: 8px;
      text-align: center;
      white-space: nowrap;
    }

    td {
      border: 1px solid ${BORDER};
      padding: 7px;
      font-size: 12px;
      vertical-align: middle;
    }

    .label {
      text-align: left;
      font-weight: normal;
    }

    .value {
      text-align: right;
    }

    .text {
      text-align: left;
    }

    .money {
      text-align: right;
      color: ${GREEN};
      font-weight: bold;
    }

    .critical {
      color: #b91c1c;
      font-weight: bold;
    }

    .warning {
      color: #b45309;
      font-weight: bold;
    }

    .ok {
      color: ${GREEN};
      font-weight: bold;
    }
  </style>
`;

const buildInfoTable = ({ metadata }) => {
  return `
    <div class="section-title">Datos del análisis</div>
    <table>
      <tr>
        <td class="label">Fecha de exportación</td>
        <td class="text">${escapeHtml(formatDateTime(new Date()))}</td>
      </tr>
      <tr>
        <td class="label">Fecha desde</td>
        <td class="text">${escapeHtml(formatDateOnly(metadata?.startDate))}</td>
      </tr>
      <tr>
        <td class="label">Fecha hasta</td>
        <td class="text">${escapeHtml(formatDateOnly(metadata?.endDate))}</td>
      </tr>
      <tr>
        <td class="label">Frecuencia</td>
        <td class="text">${escapeHtml(translateFrequency(metadata?.frec))}</td>
      </tr>
      <tr>
        <td class="label">Dispositivos</td>
        <td class="text">${escapeHtml(metadata?.devices || "-")}</td>
      </tr>
    </table>
  `;
};

const buildSummaryTable = ({ data }) => {
  const totals = data?.totals || {};

  const totalEnergy = toNumberOrZero(totals.totalEnergy);

  const nonProductiveEnergy =
    toNumberOrZero(totals.nonProductiveEnergy) ||
    toNumberOrZero(totals.passiveNightEnergy);

  const passiveBaseEnergy =
    toNumberOrZero(totals.passiveBaseEnergy) ||
    toNumberOrZero(totals.passiveNightEnergy);

  const elevatedNonProductiveEnergy =
    toNumberOrZero(totals.elevatedNonProductiveEnergy) ||
    toNumberOrZero(totals.nonProductiveAnomalyEnergy);

  const nonProductivePercentage =
    totalEnergy > 0 ? (nonProductiveEnergy / totalEnergy) * 100 : 0;

  const passiveBasePercentage =
    totalEnergy > 0 ? (passiveBaseEnergy / totalEnergy) * 100 : 0;

  const elevatedPercentage =
    totalEnergy > 0 ? (elevatedNonProductiveEnergy / totalEnergy) * 100 : 0;

  const costTotal = toNumberOrZero(totals.costTotal);
  const costNonProductive =
    toNumberOrZero(totals.costNonProductive) ||
    toNumberOrZero(totals.cost) ||
    toNumberOrZero(totals.costPassive);

  const costPassiveBase = toNumberOrZero(totals.costPassive);
  const costElevated = toNumberOrZero(totals.costElevatedNonProductive);
  const costActive = toNumberOrZero(totals.costActive);

  const activeEnergy = Math.max(totalEnergy - nonProductiveEnergy, 0);
  const activePercentage =
    totalEnergy > 0 ? (activeEnergy / totalEnergy) * 100 : 0;

  const nonProductiveCostPercentage =
    costTotal > 0 ? (costNonProductive / costTotal) * 100 : 0;

  const passiveBaseCostPercentage =
    costTotal > 0 ? (costPassiveBase / costTotal) * 100 : 0;

  const elevatedCostPercentage =
    costTotal > 0 ? (costElevated / costTotal) * 100 : 0;

  const activeCostPercentage =
    costTotal > 0 ? (costActive / costTotal) * 100 : 0;

  const eventReviewCount =
    totals.eventReviewCount ?? totals.anomalyCount ?? 0;

  const criticalAnomalyCount = totals.criticalAnomalyCount ?? 0;

  const levelReference =
    totals.nonProductivePercentage ?? totals.passivePercentage ?? nonProductivePercentage;

  return `
    <div class="section-title">Resumen energético y económico</div>
    <table>
      <thead>
        <tr>
          <th>Indicador</th>
          <th>Energía (kWh)</th>
          <th>% del total</th>
          <th>Costo estimado</th>
          <th>% del costo</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="label">Consumo total</td>
          <td class="value">${formatNumber(totalEnergy)}</td>
          <td class="value">${formatPercent(100)}</td>
          <td class="money">${formatCurrency(costTotal)}</td>
          <td class="value">${formatPercent(100)}</td>
        </tr>
        <tr>
          <td class="label">Consumo fuera de horario</td>
          <td class="value">${formatNumber(nonProductiveEnergy)}</td>
          <td class="value">${formatPercent(nonProductivePercentage)}</td>
          <td class="money">${formatCurrency(costNonProductive)}</td>
          <td class="value">${formatPercent(nonProductiveCostPercentage)}</td>
        </tr>
        <tr>
          <td class="label">Carga pasiva base</td>
          <td class="value">${formatNumber(passiveBaseEnergy)}</td>
          <td class="value">${formatPercent(passiveBasePercentage)}</td>
          <td class="money">${formatCurrency(costPassiveBase)}</td>
          <td class="value">${formatPercent(passiveBaseCostPercentage)}</td>
        </tr>
        ${elevatedNonProductiveEnergy > 0 ? `
        <tr>
          <td class="label">Consumo elevado fuera de horario</td>
          <td class="value">${formatNumber(elevatedNonProductiveEnergy)}</td>
          <td class="value">${formatPercent(elevatedPercentage)}</td>
          <td class="money">${formatCurrency(costElevated)}</td>
          <td class="value">${formatPercent(elevatedCostPercentage)}</td>
        </tr>
        ` : ""}
        <tr>
          <td class="label">Consumo dentro del horario operativo</td>
          <td class="value">${formatNumber(activeEnergy)}</td>
          <td class="value">${formatPercent(activePercentage)}</td>
          <td class="money">${formatCurrency(costActive)}</td>
          <td class="value">${formatPercent(activeCostPercentage)}</td>
        </tr>
      </tbody>
    </table>

    <div class="section-title">Indicadores principales</div>
    <table>
      <tbody>
        <tr>
          <td class="label">Carga base estimada</td>
          <td class="value">${formatNumber(data?.baseLoad)} kWh</td>
        </tr>
        <tr>
          <td class="label">Nivel de criticidad</td>
          <td class="text ok">${escapeHtml(getPassiveLevel(levelReference))}</td>
        </tr>
        <tr>
          <td class="label">% fuera de horario</td>
          <td class="value">${formatPercent(nonProductivePercentage)}</td>
        </tr>
        ${eventReviewCount > 0 ? `
        <tr>
          <td class="label">Eventos a revisar</td>
          <td class="value">${eventReviewCount}</td>
        </tr>
        ` : ""}
        ${criticalAnomalyCount > 0 ? `
        <tr>
          <td class="label">Anomalías críticas</td>
          <td class="value">${criticalAnomalyCount}</td>
        </tr>
        ` : ""}
      </tbody>
    </table>
  `;
};

const buildSavingsTable = ({ data }) => {
  const totals = data?.totals || {};

  const nonProductiveEnergy =
    toNumberOrZero(totals.nonProductiveEnergy) ||
    toNumberOrZero(totals.passiveNightEnergy);

  const nonProductiveCost =
    toNumberOrZero(totals.costNonProductive) ||
    toNumberOrZero(totals.cost) ||
    toNumberOrZero(totals.costPassive);

  if (nonProductiveEnergy <= 0 && nonProductiveCost <= 0) return "";

  const scenarios = [
    { name: "Conservador", reduction: 0.1 },
    { name: "Medio", reduction: 0.25 },
    { name: "Óptimo", reduction: 0.4 },
  ];

  const rows = scenarios
    .map((scenario) => {
      return `
        <tr>
          <td class="label">${escapeHtml(scenario.name)}</td>
          <td class="value">${formatPercent(scenario.reduction * 100, 0)}</td>
          <td class="value">${formatNumber(nonProductiveEnergy * scenario.reduction)}</td>
          <td class="money">${formatCurrency(nonProductiveCost * scenario.reduction)}</td>
        </tr>
      `;
    })
    .join("");

  return `
    <div class="section-title">Ahorro potencial sobre consumo fuera de horario</div>
    <table>
      <thead>
        <tr>
          <th>Escenario</th>
          <th>Reducción estimada</th>
          <th>Ahorro energético (kWh)</th>
          <th>Ahorro económico estimado</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
};

const buildStatisticsTable = ({ statistics }) => {
  if (!statistics || statistics.count == null || statistics.count < 2) return "";

  const variationLabel = getVariationLabel(statistics.stdDev, statistics.mean);

  const rows = [
    ["Períodos analizados", statistics.count, 0, ""],
    ["Consumo promedio", statistics.mean, 3, "kWh"],
    ["Consumo habitual", statistics.median, 3, "kWh"],
    ["Consumo máximo", statistics.max, 3, "kWh"],
    ["Variación del consumo", variationLabel, null, ""],
    ["Límite para revisar", statistics.thresholdExcess, 3, "kWh"],
  ]
    .map(([label, value, decimals, unit]) => {
      const formattedValue =
        decimals == null ? escapeHtml(value) : formatNumber(value, decimals);

      return `
        <tr>
          <td class="label">${escapeHtml(label)}</td>
          <td class="value">${formattedValue}${unit ? ` ${unit}` : ""}</td>
        </tr>
      `;
    })
    .join("");

  return `
    <div class="section-title">Resumen del comportamiento analizado</div>
    <table>
      <thead>
        <tr>
          <th>Indicador</th>
          <th>Valor</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
};

const buildAnomaliesTable = ({ anomalies }) => {
  if (!Array.isArray(anomalies) || anomalies.length === 0) return "";

  const sorted = [...anomalies].sort((a, b) => {
    const aTime = new Date(
      getField(a, ["timestamp", "fecha_hora", "date", "created_at"])
    ).getTime();

    const bTime = new Date(
      getField(b, ["timestamp", "fecha_hora", "date", "created_at"])
    ).getTime();

    return (
      (Number.isFinite(bTime) ? bTime : 0) -
      (Number.isFinite(aTime) ? aTime : 0)
    );
  });

  const rows = sorted
    .map((item) => {
      const rawTimestamp =
        getField(item, ["timestamp", "fecha_hora"]) ||
        getField(item, ["date", "created_at"]);

      const classification =
        getField(item, ["classification", "clasificacion"]) || "-";

      const translatedClassification = translateAnomalyLevel(classification);

      const classificationClass =
        translatedClassification === "Crítico"
          ? "critical"
          : translatedClassification === "Anómalo" ||
            translatedClassification === "Revisar"
          ? "warning"
          : "";

      return `
        <tr>
          <td class="text">${escapeHtml(formatDateTime(rawTimestamp))}</td>
          <td class="value">${formatNumber(
            getField(item, [
              "realValue",
              "real_kw",
              "actual_kw",
              "real_energy",
              "real",
            ]),
            3
          )}</td>
          <td class="value">${formatNumber(
            getField(item, [
              "expectedValue",
              "expected_kw",
              "estimated_kw",
              "expected_energy",
              "expected",
            ]),
            3
          )}</td>
          <td class="value">${formatNumber(
            getField(item, ["difference", "difference_kw", "residual", "diff"]),
            3
          )}</td>
          <td class="value">${formatNumber(
            getField(item, ["zScore", "z_score"]),
            2
          )}</td>
          <td class="text ${classificationClass}">${escapeHtml(translatedClassification)}</td>
          <td class="text">${escapeHtml(
            translateOrigin(getField(item, ["source", "origen"]))
          )}</td>
        </tr>
      `;
    })
    .join("");

  return `
    <div class="section-title">Eventos a revisar</div>
    <table>
      <thead>
        <tr>
          <th>Fecha / hora</th>
          <th>Consumo real</th>
          <th>Consumo esperado</th>
          <th>Diferencia</th>
          <th>Nivel</th>
          <th>Clasificación</th>
          <th>Origen</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
};

const buildModelMetricsTable = ({ modelMetrics }) => {
  if (!modelMetrics) return "";

  return `
    <div class="section-title">Resumen del modelo de referencia</div>
    <table>
      <thead>
        <tr>
          <th>Indicador</th>
          <th>Valor</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="label">Diferencia promedio frente al comportamiento esperado</td>
          <td class="value">${formatNumber(modelMetrics.mae, 3)} kWh</td>
        </tr>
        <tr>
          <td class="label">Diferencia máxima estimada del modelo</td>
          <td class="value">${formatNumber(modelMetrics.rmse, 3)} kWh</td>
        </tr>
        <tr>
          <td class="label">Períodos usados por el modelo</td>
          <td class="value">${formatNumber(
            modelMetrics.nRecords ?? modelMetrics.records,
            0
          )}</td>
        </tr>
        <tr>
          <td class="label">Descripción</td>
          <td class="text">${escapeHtml(modelMetrics.description || "-")}</td>
        </tr>
      </tbody>
    </table>
  `;
};

const buildSeriesTable = ({ data }) => {
  const series = Array.isArray(data?.series) ? data.series : [];

  if (series.length === 0) return "";

  const rows = series
    .map((point) => {
      const elevatedNonProductive = isElevatedNonProductivePoint(point);

      const level = translateAnomalyLevel(point.anomaly_level);

      const levelClass =
        level === "Crítico"
          ? "critical"
          : level === "Anómalo" || level === "Revisar"
          ? "warning"
          : "";

      return `
        <tr>
          <td class="text">${escapeHtml(formatDateTime(point.timestamp))}</td>
          <td class="text">${escapeHtml(translatePeriodType(point.period_type))}</td>
          <td class="value">${formatNumber(toNumberOrNull(point.total_energy), 3)}</td>
          <td class="value">${formatNumber(toNumberOrNull(point.passive_energy), 3)}</td>
          <td class="value">${formatNumber(
            elevatedNonProductive ? toNumberOrZero(point.total_energy) : 0,
            3
          )}</td>
          <td class="value">${formatNumber(toNumberOrNull(point.base_energy), 3)}</td>
          <td class="text">${point.is_passive ? "Sí" : "No"}</td>
          <td class="text ${levelClass}">${escapeHtml(level)}</td>
          <td class="text">${escapeHtml(point.passive_reason || "-")}</td>
          <td class="money">${formatCurrency(toNumberOrNull(point.cost_total))}</td>
          <td class="money">${formatCurrency(toNumberOrNull(point.cost_passive))}</td>
          <td class="text">${escapeHtml(point.band || "-")}</td>
        </tr>
      `;
    })
    .join("");

  return `
    <div class="section-title">Detalle horario analizado</div>
    <table>
      <thead>
        <tr>
          <th>Fecha / hora</th>
          <th>Período</th>
          <th>Consumo total</th>
          <th>Carga pasiva base</th>
          <th>Consumo elevado fuera de horario</th>
          <th>Carga base estimada</th>
          <th>Cercano a carga base</th>
          <th>Nivel</th>
          <th>Motivo</th>
          <th>Costo total</th>
          <th>Costo carga pasiva base</th>
          <th>Banda</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
};

const downloadExcelHtml = (html, fileName) => {
  const blob = new Blob([html], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportPassiveLoadExcel = ({
  data,
  passiveStatistics,
  passiveAnomalies,
  modelMetrics,
  metadata = {},
}) => {
  if (!data) return;

  const statistics = passiveStatistics || data?.statistics || data?.stats;

  const anomalies =
    passiveAnomalies || data?.anomalies || data?.totals?.anomalies;

  const metrics = modelMetrics || data?.modelMetrics;

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="UTF-8" />
        ${tableStyles}
      </head>
      <body>
        <table>
          <tr>
            <td class="report-title" colspan="12">
              SIGER - Análisis de Carga Pasiva
            </td>
          </tr>
        </table>

        ${buildInfoTable({ metadata })}
        ${buildSummaryTable({ data })}
        ${buildSavingsTable({ data })}
        ${buildStatisticsTable({ statistics })}
        ${buildAnomaliesTable({ anomalies })}
        ${buildModelMetricsTable({ modelMetrics: metrics })}
        ${buildSeriesTable({ data })}
      </body>
    </html>
  `;

  const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");

  downloadExcelHtml(html, `carga_pasiva_${stamp}.xls`);
};

export default exportPassiveLoadExcel;